import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import CreatorHeader from '../../components/creator/CreatorHeader';

import xdate from '../../../../../util/date.js';

type Video = {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string;
  category: string;
  tags: string[];
  status: string;
  duration: number;
  file_size: number;
  ts?: {insert: Date, modified: Date};
};

type VideoCampaign = {
  id: string;
  campaign_id: string;
  processing_status: string;
  processed_video_url?: string;
  ts?: {insert: Date, modified: Date};
  brand_name?: string;  
  campaigns?: {
    id: string;
    name: string;
    category: string;
  };
};

export default function VideoDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<Video | null>(null);
  const [videoCampaigns, setVideoCampaigns] = useState<VideoCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideo();
    fetchVideoCampaigns();
  }, [id]);

  const fetchVideo = async () => {
    try {
      const result = await back_app.video_get(id!);

      if (!result.err) {
        setVideo(result);
      } else {
        navigate('/creator/videos');
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      navigate('/creator/videos');
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoCampaigns = async () => {
    try {
      const result = await back_app.vcamp_list({video_id: id!});

      if (!result.err) {
        setVideoCampaigns(result || []);
      }
    } catch (error) {
      console.error('Error fetching video campaigns:', error);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const result = await back_app.video_delete(id!);

      if (!result.err) {
        alert('Video deleted successfully');
        navigate('/creator/videos');
      } else {
        alert('Error deleting video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error deleting video');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Video not found</h2>
            <button
              onClick={() => navigate('/creator/videos')}
              className="text-blue-600 hover:underline"
            >
              Back to videos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <button
          onClick={() => navigate('/creator/videos')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <span>←</span>
          <span>Back to Videos</span>
        </button>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {}
          <div className="lg:col-span-2 space-y-6">
            {}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {}
              <div className="bg-black flex items-center justify-center p-4">
                <div className="w-full max-w-md mx-auto" style={{ aspectRatio: '9/16' }}>
                  <video
                    src={video.video_url}
                    controls
                    className="w-full h-full rounded-lg"
                    poster={video.thumbnail_url}
                  />
                </div>
              </div>

              {}
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      {video.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span>{video.ts?.insert ? xdate.fmt_lbl_short(video.ts.insert) : ''}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        video.status === 'published' ? 'bg-green-500 text-white' :
                        video.status === 'processing' ? 'bg-yellow-500 text-white' :
                        'bg-gray-500 text-white'
                      }`}>
                        {video.status}
                      </span>
                    </div>
                  </div>
                </div>

                {video.description && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">{video.description}</p>
                  </div>
                )}

                {video.category && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Category</h3>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded">
                      {cat_id2lbl(video.category)}
                    </span>
                  </div>
                )}

                {video.tags && video.tags.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {video.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {}
            {videoCampaigns.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                  </svg>
                  <span>Connected Campaigns ({videoCampaigns.length})</span>
                </h3>

                <div className="space-y-3">
                {videoCampaigns.map((vc) => (
                  <div
                    key={vc.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/creator/video-processing?id=${vc.id}&video_id=${video.id}&campaign_id=${vc.campaign_id}`)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {}
                        {vc.brand_name && (
                          <p className="text-xs text-gray-500 mb-1">
                            {vc.brand_name}
                          </p>
                        )}
                        {}
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {vc.campaigns?.name || 'Campaign'}
                        </h4>
                        {vc.campaigns?.category && (
                          <p className="text-xs text-gray-600 mt-0.5">{cat_id2lbl(vc.campaigns.category)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          vc.processing_status === 'completed' ? 'bg-green-100 text-green-700' :
                          vc.processing_status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          vc.processing_status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {vc.processing_status}
                        </span>
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}
          </div>

          {}
          <div className="space-y-6">
            {}
            <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
              <h3 className="font-bold text-gray-900 mb-4">Video Info</h3>
              
              <div className="space-y-3">
                {video.duration > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">Duration</span>
                    <span className="font-semibold text-gray-900">
                      {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">File Size</span>
                  <span className="font-semibold text-gray-900">
                    {(video.file_size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600">Uploaded</span>
                  <span className="font-semibold text-gray-900">
                    {video.ts?.insert ? xdate.fmt_lbl_short(video.ts.insert) : ''}
                  </span>
                </div>
              </div>
            </div>

            {}
            <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
              <h3 className="font-bold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/creator/campaigns?video_id=${video.id}`)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add to Campaign</span>
                </button>

                <button
                  onClick={handleDelete}
                  className="w-full px-6 py-3 border-2 border-red-300 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-all"
                >
                  Delete Video
                </button>
              </div>
            </div>

            {}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200 p-4 sm:p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
                </svg>
                <span>Statistics</span>
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-blue-200">
                  <span className="text-sm text-gray-700">Active Campaigns</span>
                  <span className="text-lg font-bold text-blue-600">
                    {videoCampaigns.length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-blue-200">
                  <span className="text-sm text-gray-700">Completed</span>
                  <span className="text-lg font-bold text-green-600">
                    {videoCampaigns.filter(vc => vc.processing_status === 'completed').length}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700">Processing</span>
                  <span className="text-lg font-bold text-yellow-600">
                    {videoCampaigns.filter(vc => 
                      vc.processing_status === 'processing' || 
                      vc.processing_status === 'analyzing'
                    ).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}